/**
 * HotspotSplitsCard — Byzantine Review Fix / heliumtracker-web#24 Nacharbeit
 *
 * Zeigt die ECHTEN Provisions-Regeln (host_split_rules) dieses Hotspots.
 * Vorher waren die Anteile hartcodiert (85%/15% "Delegate") ohne Netzwerk-Call
 * — das ist ueberholt: es kann eine echte host_split_rule mit abweichenden
 * Prozenten existieren. Kein-Regeln-Fall: ehrlicher Empty-State mit Link zu
 * /splits, KEINE erfundenen Prozente.
 *
 * heliumtracker-web#24: Der Hint versprach vorher einen falschen automatischen
 * Auszahlungstermin ("alle 15 Tage, naechste Auszahlung: ..."). v2 hat keinen
 * solchen automatischen On-Chain-Auszahlungszyklus mehr — Auszahlungen laufen
 * ueber manuell erfasste Zahlungen (siehe features/payments). Der Hint macht
 * daher keine Zeitangabe mehr, nur die reine Split-Aussage.
 */
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useHotspotSplitRules, useHosts } from '../splits/useSplitRules'
import { splitRuleStatus } from '../splits/splitRuleHelpers'

// --- Types ---

export type SplitParticipant = {
  name: string
  share: number // 0-100 percentage
  isOwner?: boolean
}

// --- SplitsBar ---

type SplitsBarProps = {
  participants: SplitParticipant[]
}

function SplitsBar({ participants }: SplitsBarProps) {
  const total = participants.reduce((sum, p) => sum + p.share, 0)
  const isValid = Math.abs(total - 100) < 0.01

  const { t } = useTranslation('hotspots')

  if (!isValid) {
    return (
      <div className="muted" style={{ fontSize: 12 }}>
        {t('splits.invalidConfig')}
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <div className="splits-bar">
        {participants.map((participant, index) => (
          <div
            key={index}
            style={{
              width: `${participant.share}%`,
              background: participant.isOwner
                ? 'var(--gradient-brand)'
                : 'var(--accent)',
            }}
          />
        ))}
      </div>
      <div className="spread" style={{ marginTop: 8, fontSize: 12 }}>
        {participants.map((participant, index) => (
          <span key={index} className="row-tight">
            <span
              className="dot"
              style={{
                background: participant.isOwner
                  ? 'var(--primary)'
                  : 'var(--accent)',
              }}
            />
            {participant.name} · {participant.share}%
          </span>
        ))}
      </div>
    </div>
  )
}

// --- Hint ---

function Hint() {
  const { t } = useTranslation('hotspots')

  return (
    <div className="hint">
      <span>{t('splits.hint')}</span>
    </div>
  )
}

// --- HotspotSplitsCard ---

type Props = {
  hotspotId: string | number
}

export function HotspotSplitsCard({ hotspotId }: Props) {
  const { t } = useTranslation('hotspots')
  const rulesQuery = useHotspotSplitRules(hotspotId)
  const hostsQuery = useHosts()

  // Nur aktuell aktive %-Regeln fliessen in die Split-Visualisierung ein —
  // Fixbetrags-Regeln haben keinen Anteil in Prozent und geplante/abgelaufene
  // Regeln gelten aktuell nicht.
  const activePercentageRules = (rulesQuery.data ?? []).filter(
    (rule) => rule.split_type === 'percentage' && splitRuleStatus(rule) === 'active',
  )

  if (rulesQuery.isLoading) {
    return (
      <div className="card card-pad" aria-label={t('splits.cardLabel')}>
        <h3 className="section-heading" style={{ marginBottom: 4 }}>
          {t('splits.heading')}
        </h3>
        <p className="muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
          {t('splits.loading')}
        </p>
      </div>
    )
  }

  if (activePercentageRules.length === 0) {
    return (
      <div className="card card-pad" aria-label={t('splits.cardLabel')}>
        <h3 className="section-heading" style={{ marginBottom: 4 }}>
          {t('splits.heading')}
        </h3>
        <p className="muted" style={{ fontSize: 12, margin: '2px 0 14px' }}>
          {t('splits.emptyState')}
        </p>
        <Link to="/splits" className="btn btn-sm">
          {t('splits.emptyStateCta')}
        </Link>
      </div>
    )
  }

  const hostShares: SplitParticipant[] = activePercentageRules.map((rule) => ({
    name:
      hostsQuery.data?.find((h) => h.id === rule.host_id)?.name ??
      t('splits.hostFallback', { id: rule.host_id ?? '?' }),
    // Backend liefert Decimal-Spalten als JSON-String (Rails BigDecimal#as_json),
    // trotz API-Typ `number | null` — ohne Number() addiert "+" hier Strings
    // ("0" + "10.0" = "010.0") statt Prozente, siehe #121.
    share: Number(rule.percentage ?? 0),
  }))
  const hostTotal = hostShares.reduce((sum, p) => sum + p.share, 0)
  const ownerShare = Math.max(0, 100 - hostTotal)

  const effectiveParticipants: SplitParticipant[] =
    ownerShare > 0
      ? [{ name: t('splits.defaultYou'), share: ownerShare, isOwner: true }, ...hostShares]
      : hostShares

  return (
    <div className="card card-pad" aria-label={t('splits.cardLabel')}>
      <h3 className="section-heading" style={{ marginBottom: 4 }}>
        {t('splits.heading')}
      </h3>
      <p className="muted" style={{ fontSize: 12, margin: '2px 0 14px' }}>
        {t('splits.descFallback', { pct: hostTotal })}
      </p>

      <SplitsBar participants={effectiveParticipants} />
      <Hint />
    </div>
  )
}
